<script lang="ts">
/**
 *  # Usage
 *
 *    Can be called either as:
 *      <base-link url="urlValue">..</base-link>
 *    Or
 *      <base-link v-bind="myObject"
 *
 *    This decoration ease the process of creation of links who
 *    can either be a <nuxt-link> (provided with `route` props)
 *    or a native <a> (provided with `url` props)
 *
 *
 *  # Use cases: common
 *
 *    According to provided props, output will either be:
 *
 *      1. `route`    → <nuxt-link>
 *      2. `callback` → <a> functional
 *      3. `url`      → <a> hypertext
 *
 *
 *  # Content hydration
 *
 *    According to component integration, content will be either be:
 *
 *      1. output by props `content`
 *      2. output by default slot
 *
 *
 *  # Use case: differenciate results if user click with "open in new tabs"
 *
 *    <ui-link
 *       :url="url_opened_in_new_tabs"
 *       @click.prevent="method_executed_for_conventional_click"
 *    />
 *
 */
import type { SlotsType } from 'vue'
import { isEmpty } from '~/utils/lib/core/prelude'
import type { ELEVATION, FOCUS_RING, STATUS, UNDERLINE, VARIANT } from '~/utils/lib/layout/ui'

export default defineComponent({
  props: {
    status: {
      type: String as PropType<STATUS>,
      required: false,
      default: 'enabled' satisfies STATUS
    },
    url: {
      type: String,
      required: false,
      default: undefined,
    },
    // @link https://nuxtjs.org/api/components-nuxt-link/
    // @link https://router.vuejs.org/api/#router-link-props
    route: {
      type: [String, Object, Function],
      required: false,
      default: undefined
    },
    callback: {
      type: Function,
      required: false,
      default: undefined
    },
    content: {
      type: String,
      required: false,
      default: undefined
    },
    // Identical to "bootstrap-vue-next" `variant` props on <b-button>
    variant: {
      type: String as PropType<VARIANT>,
      required: false,
      default: undefined
    },
    bulky: {
      type: Boolean,
      required: false,
      default: false
    },
    elevation: {
      type: String as PropType<ELEVATION>,
      required: false,
      default: undefined,
    },
    noAnimation: {
      type: Boolean,
      required: false,
      default: false
    },
    underline: {
      type: String as PropType<UNDERLINE>,
      required: false,
      default: 'none' satisfies UNDERLINE
    },
    focusRing: {
      type: String as PropType<FOCUS_RING>,
      required: false,
      default: 'none' satisfies FOCUS_RING
    },
    focusRingVariant: {
      type: String as PropType<VARIANT | undefined>,
      required: false,
      default: undefined
    },
  },
  emits: {
    'click': (event: PointerEvent) => true,
  },
  slots: Object as SlotsType<{
    'default': {},
  }>,
  computed: {
    computedClassName() {
      return [
        isEmpty(this.variant) ? '' : `ui-link--${this.variant}`,
        isEmpty(this.bulky) ? '' : 'ui-link--bulky',
        isEmpty(this.elevation) ? '' : `ui-link--${this.elevation}`,
        isEmpty(this.noAnimation) ? '' : 'ui-link--no-animation',
        `ui-link--underline-${this.underline}`,
        `ui-link--focus-ring-${this.focusRing}`,
        isEmpty(this.focusRingVariant) ?
          '' :
          `ui-link--focus-ring-variant-${this.focusRingVariant}`,
        this.status === 'idled' ? 'ui-link--idled' : '',
      ]
    },
  },
  methods: {
    onClick(event: PointerEvent) {
      if (this.callback)
        return this.callback()
      return this.$emit('click', event)
    },
  }
})
</script>

<template>

  <!--
    Disabled Link
    Represents a placeholder link meaning that a link would have
    been on this element if some condition is met
    @link https://www.scottohara.me/note/2019/07/17/placeholder-link.html
  -->
  <a
    v-if="status === 'disabled'"
    :class="[
      'ui-link',
      'ui-link--disabled',
      ...computedClassName,
    ]"
  >
    <template v-if="content">
      {{ content }}
    </template>

    <slot v-else />
  </a>

  <nuxt-link
    v-else-if="route"
    v-bind="$attrs"
    :to="route"
    :class="[
      'ui-link',
      ...computedClassName,
    ]"
    v-on="{
      click: onClick,
    }"
  >
    <template v-if="content">
      {{ content }}
    </template>

    <slot v-else />
  </nuxt-link>

  <a
    v-else
    v-bind="$attrs"
    :href="url"
    :class="[
      'ui-link',
      ...computedClassName
    ]"
  >
    <template v-if="content">
      {{ content }}
    </template>

    <slot v-else />
  </a>
</template>

<style lang="sass">
.ui-link
  @include clickable
  position: relative

  // Plus overrinding "bootstrap-vue-next" hover.scss annoying `a:hover` rule
  @each $theme, $value in $palette-semantic

    &--#{ $theme },
    &--#{ $theme }:hover
      color: $value

  &--disabled
    @include unclickable

  &--idled
    @include unclickable

  /// Bulky structure
  ///
  &--bulky
    border: 1px solid transparent
    border-radius: $btn-border-radius
    padding: $input-btn-padding-y $input-btn-padding-x
    transition: $btn-transition

  /// Elevations
  ///

  // overlay preparation (with elevation only: conservative design)
  &--level-0::before,
  &--level-1::before,
  &--level-2::before
    @include virtual-content
    @include inset
    position: absolute
    border-radius: $btn-border-radius
    background-color: transparent
    z-index: -2

  @each $theme, $value in $palette-semantic

    &--level-0:active#{&}--#{$theme},
    &--level-0:focus#{&}--#{$theme},
    &--level-0:hover#{&}--#{$theme}
      color: darken($value, 15%)

  &--level-1:active::before,
  &--level-1:focus::before,
  &--level-1:hover::before
    background-color: $btn-overlay-bg

  &--level-2::before
    background-color: $btn-overlay-bg

  &--level-2:active::before,
  &--level-2:focus::before,
  &--level-2:hover::before
    background-color: $btn-overlay-bg-hover

  /// Status: suppress elevation feedback when non-interactive
  ///
  &--disabled#{&}--level-1:active::before,
  &--disabled#{&}--level-1:focus::before,
  &--disabled#{&}--level-1:hover::before,
  &--idled#{&}--level-1:active::before,
  &--idled#{&}--level-1:focus::before,
  &--idled#{&}--level-1:hover::before
    content: initial

  &--disabled#{&}--level-2:active::before,
  &--disabled#{&}--level-2:focus::before,
  &--disabled#{&}--level-2:hover::before,
  &--idled#{&}--level-2:active::before,
  &--idled#{&}--level-2:focus::before,
  &--idled#{&}--level-2:hover::before
    background-color: $btn-overlay-bg

  // elevation overlay animation
  &:not(&--no-animation)::before
    transition: $transition-base

  /// Underline
  ///
  &--underline-hover:active,
  &--underline-hover:focus,
  &--underline-hover:hover
    text-decoration: underline

  &--underline-always,
  &--underline-always:active,
  &--underline-always:focus,
  &--underline-always:hover
    text-decoration: underline

  /// Focus ring
  ///
  /// Dedicated ::after layer — elevation overlay stays on ::before.

  &--focus-ring-active::after,
  &--focus-ring-always::after
    @include virtual-content
    @include inset
    position: absolute
    border-radius: $btn-border-radius
    background-color: transparent
    z-index: -1
    pointer-events: none

  &--focus-ring-active::after
    box-shadow: 0 0 0 0 transparent

  &--focus-ring-always::after
    transition: none

  &:not(&--no-animation)#{&}--focus-ring-active::after
    transition: $transition-base

  &--focus-ring-active:not(#{&}--disabled):not(#{&}--idled)
    &:focus::after,
    &:active::after
      box-shadow: 0 0 0 2px mix-alpha($focus-ring-color, 50%)

  &--focus-ring-always:not(#{&}--disabled):not(#{&}--idled)::after
    box-shadow: 0 0 0 2px mix-alpha($focus-ring-color, 50%)

  @include focus-ring-variant-theme

</style>
